import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";
import { ArrowLeft, MessageCircle, Send, Loader2, Feather, User } from "lucide-react";

function fmt(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

const REACTIONS = [
  { key: "pray", emoji: "🙏", label: "Give thanks" },
  { key: "heart", emoji: "❤️", label: "Love this" },
];

function ReplyReactions({ reply }) {
  const [counts, setCounts] = useState(() => ({ ...(reply.reactions || {}) }));
  const [reacted, setReacted] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`forumReacted_${reply.id}`) || "{}"); }
    catch { return {}; }
  });

  const react = async (key) => {
    if (reacted[key]) return;
    setCounts((c) => ({ ...c, [key]: (c[key] || 0) + 1 }));
    const next = { ...reacted, [key]: true };
    setReacted(next);
    localStorage.setItem(`forumReacted_${reply.id}`, JSON.stringify(next));
    try {
      const r = await api.post(`/forum/replies/${reply.id}/react`, { emoji: key });
      if (r?.data?.reactions) setCounts(r.data.reactions);
    } catch { /* keep optimistic count */ }
  };

  return (
    <div className="flex items-center gap-2 mt-3 pl-9" data-testid="forum-reply-reactions">
      {REACTIONS.map((r) => {
        const active = !!reacted[r.key];
        const n = counts[r.key] || 0;
        return (
          <button key={r.key} onClick={() => react(r.key)} disabled={active} title={r.label}
            data-testid={`reply-react-${r.key}`}
            className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-colors ${active ? "border-gold/50 bg-gold/10 text-gold cursor-default" : "border-white/10 text-bone/50 hover:border-gold/40 hover:text-gold"}`}>
            <span className="text-sm leading-none">{r.emoji}</span>
            {n > 0 && <span className="tabular-nums">{n}</span>}
          </button>
        );
      })}
    </div>
  );
}

export default function ForumTopic() {
  const { id } = useParams();
  const [topic, setTopic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState(() => localStorage.getItem("forumName") || "");
  const [body, setBody] = useState("");
  const [hp, setHp] = useState("");
  const [busy, setBusy] = useState(false);
  const [challenge] = useState(() => ({ a: 2 + Math.floor(Math.random() * 7), b: 1 + Math.floor(Math.random() * 6) }));
  const [answer, setAnswer] = useState("");

  const load = () => api.get(`/forum/topics/${id}`).then((r) => setTopic(r.data)).catch(() => setTopic(null)).finally(() => setLoading(false));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || body.trim().length < 2) { toast.error("Please add your name and a message"); return; }
    if (parseInt(answer, 10) !== challenge.a + challenge.b) { toast.error("Please answer the verification question correctly"); return; }
    setBusy(true);
    try {
      await api.post(`/forum/topics/${id}/replies`, { author_name: name.trim(), body: body.trim(), website: hp });
      localStorage.setItem("forumName", name.trim());
      setBody(""); setAnswer("");
      toast.success("Your voice has been added to the circle");
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Could not post your reply");
    }
    finally { setBusy(false); }
  };

  if (loading) return <main className="bg-onyx min-h-screen pt-32 text-center text-bone/50"><Loader2 className="animate-spin inline" /> Loading discussion...</main>;
  if (!topic) return <main className="bg-onyx min-h-screen pt-32 text-center text-bone/50" data-testid="forum-topic-missing">Discussion not found. <Link to="/forum" className="text-gold underline">Back to the forum</Link></main>;

  return (
    <main className="bg-onyx min-h-screen pt-24 pb-20" data-testid="forum-topic-page">
      <div className="max-w-3xl mx-auto px-5 md:px-8">
        <Link to="/forum" className="inline-flex items-center gap-2 text-bone/50 hover:text-gold text-sm mb-8 transition-colors" data-testid="forum-back">
          <ArrowLeft size={16} /> All Discussions
        </Link>

        <span className="text-[10px] uppercase tracking-widest text-gold border border-gold/30 px-2 py-0.5">{topic.category}</span>
        <h1 className="font-serif text-3xl md:text-4xl text-bone mt-4 mb-3 leading-tight" data-testid="forum-topic-title">{topic.title}</h1>
        {topic.summary && <p className="text-bone/55 text-lg">{topic.summary}</p>}

        {/* Opening commentary from the Council */}
        <div className="mt-8 bg-surface border-l-2 border-gold p-6 md:p-8 grain" data-testid="forum-opening-commentary">
          <div className="flex items-center gap-2 mb-4">
            <Feather size={16} className="text-gold" />
            <span className="text-sm text-gold font-serif italic">{topic.author}</span>
          </div>
          {topic.opening_commentary.split("\n").filter(Boolean).map((p, i) => (
            <p key={i} className="text-bone/80 leading-relaxed mb-3 last:mb-0">{p}</p>
          ))}
        </div>

        {/* Replies */}
        <div className="mt-12">
          <h2 className="font-serif text-xl text-bone mb-6 flex items-center gap-2">
            <MessageCircle size={18} className="text-gold" /> {topic.replies.length} {topic.replies.length === 1 ? "Voice" : "Voices"} in the Circle
          </h2>
          {topic.replies.length === 0 ? (
            <p className="text-bone/40 text-sm mb-8" data-testid="forum-no-replies">Be the first to share your reflection.</p>
          ) : (
            <div className="space-y-4 mb-10">
              {topic.replies.map((r) => (
                <div key={r.id} className="bg-surface border border-white/10 p-5" data-testid="forum-reply">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-7 h-7 rounded-full bg-gold/15 text-gold flex items-center justify-center shrink-0"><User size={14} /></span>
                    <span className="text-bone font-medium text-sm">{r.author_name}</span>
                    <span className="text-bone/30 text-xs">· {fmt(r.created_at)}</span>
                  </div>
                  <p className="text-bone/75 leading-relaxed whitespace-pre-wrap pl-9">{r.body}</p>
                  <ReplyReactions reply={r} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Reply form */}
        <form onSubmit={submit} className="bg-surface border border-white/10 p-6 md:p-8 space-y-4" data-testid="forum-reply-form">
          <h3 className="font-serif text-lg text-bone">Add Your Voice</h3>
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} placeholder="Your name or handle"
            className="w-full bg-black/50 border border-white/10 px-4 py-2.5 text-bone focus:outline-none focus:border-gold/50 text-sm" data-testid="forum-reply-name" />
          <textarea value={body} onChange={(e) => setBody(e.target.value)} maxLength={4000} rows={5} placeholder="Share your reflection, experience, or wisdom..."
            className="w-full bg-black/50 border border-white/10 px-4 py-2.5 text-bone focus:outline-none focus:border-gold/50 text-sm" data-testid="forum-reply-body" />
          {/* Honeypot — hidden from humans, catches bots */}
          <input type="text" value={hp} onChange={(e) => setHp(e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true"
            name="website" data-testid="forum-hp"
            style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0 }} />
          <div className="flex items-center gap-3 bg-black/40 border border-white/10 px-4 py-2.5">
            <span className="text-bone/60 text-sm">Quick check: what is <span className="text-gold font-medium">{challenge.a} + {challenge.b}</span>?</span>
            <input value={answer} onChange={(e) => setAnswer(e.target.value)} inputMode="numeric" placeholder="?"
              className="w-16 bg-black/50 border border-white/10 px-2 py-1 text-bone text-sm text-center focus:outline-none focus:border-gold/50" data-testid="forum-human-check" />
          </div>
          <div className="flex items-center justify-between">
            <p className="text-bone/40 text-xs">Speak in love. No login required.</p>
            <button type="submit" disabled={busy} className="inline-flex items-center gap-2 bg-gold text-black px-6 py-2.5 text-sm font-medium hover:bg-gold/90 disabled:opacity-50 transition-colors" data-testid="forum-reply-submit">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />} Post
            </button>
          </div>
        </form>
      </div>
    </main>
  );
}
